import { useCallback, useMemo } from 'react';

import { useGetClientSubscriptionsQuery } from '@app/api/Subscriptions';
import { groupClientSubscriptions } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/utils';
import type {
  Service,
  UseSubscriptionQuantityLimitsProps,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { calculateSubscriptionQuantityLimit } from '@app/components/RecordModal/components/RecordDetails/utils/subscriptionQuantityLimit';

/**
 * Пределы количества услуг с абонемента в «Услугах» окна записи. Остаток
 * берётся из того же запроса GetClientSubscriptions, что и «Абонементы
 * клиента» (общий кэш RTK Query). Пока запрос идёт или вернул ошибку,
 * свободных единиц нет: после «Сохранить» кэш сбрасывается
 * (invalidateClientSubscriptions), и прошлый остаток завысил бы предел.
 * Уменьшить количество можно и без ответа.
 */
export const useSubscriptionQuantityLimits = ({
  clientId,
  activeBranchId,
  savedServices,
}: UseSubscriptionQuantityLimitsProps) => {
  const { currentData, error, isFetching } = useGetClientSubscriptionsQuery(
    { ActiveBranchId: activeBranchId, clientId: clientId ?? 0 },
    { skip: !clientId || activeBranchId === null }
  );

  const subscriptions = useMemo(
    () =>
      groupClientSubscriptions(error || isFetching ? undefined : currentData),
    [currentData, error, isFetching]
  );

  const getSubscriptionQuantityLimit = useCallback(
    (service: Service) =>
      calculateSubscriptionQuantityLimit({
        service,
        savedServices,
        subscriptions,
      }),
    [savedServices, subscriptions]
  );

  return { getSubscriptionQuantityLimit };
};
